<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-col gap-2">
      <label class="font-semibold" :for="name"
        >{{ label }}<span v-if="required" class="text-red-600">*</span></label
      >
      <Multiselect class="p-[5px]" v-model="parentInputValue" :options="transformedArray" />
      <p class="text-red-500" v-show="parentErrorMessage || parentMeta.valid">
        {{ parentErrorMessage }}
      </p>
    </div>
    <div v-if="parentInputValue" class="flex flex-col gap-2">
      <label class="font-semibold" :for="child?.label">{{ child?.label }}</label>
      <Multiselect
        class="p-[5px]"
        v-model="childInputValue"
        :options="transformedArrayChildren"
      ></Multiselect>
      <p class="text-red-500" v-show="childErrorMessage || childMeta.valid">
        {{ childErrorMessage }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import { useField } from 'vee-validate';
import * as yup from 'yup';
import { ref, computed, onMounted, watch } from 'vue';
import Multiselect from '@vueform/multiselect';

const { t } = useI18n();

const props = defineProps({
  value: { type: String, default: undefined },
  label: { type: String, required: true },
  name: { type: String, required: true },
  required: { type: Boolean, default: true },
  options: { type: Array },
  property: { type: Number, default: undefined },
  defaultParentValue: {},
  defaultChildValue: {},
});

const child = ref(null);

// ✅ rules: если поле необязательное — можно ослабить, но ты сейчас required=true по умолчанию
const rules = yup.number().min(1, 'Choose at least one').required(t('warning.REQUIRED_MESSAGE'));

// ✅ parent field
const {
  meta: parentMeta,
  value: parentInputValue,
  errorMessage: parentErrorMessage,
  validate: parentValidate,
} = useField(props.name, rules, {
  initialValue: props.defaultParentValue ?? props.value,
});

// ✅ child field (ВАЖНО: имя должно быть стабильным и НЕ undefined)
const childFieldName = `${props.name}__child`;
const {
  meta: childMeta,
  value: childInputValue,
  errorMessage: childErrorMessage,
  validate: childValidate,
} = useField(childFieldName, rules, {
  initialValue: props.defaultChildValue ?? null,
});

// ✅ map parents to multiselect options
const transformedArray = computed(() => {
  return (props.options || []).map(item => ({
    value: item.id,
    label: item.name,
    children: item.children,
  }));
});

// ✅ children list for selected parent
const transformedArrayChildren = computed(() => {
  return (child.value?.children || []).map(item => ({
    value: item.id,
    label: item.name,
  }));
});

function normalizeToId(v) {
  if (v == null) return null;
  if (typeof v === 'object') return v.value ?? v.id ?? null;
  return v;
}

function getParent(selectedValue) {
  const parentId = normalizeToId(selectedValue);
  const rawParent = (props.options || []).find(el => el.id === parentId);

  // если не нашли — сбросим всё безопасно
  if (!rawParent) {
    child.value = null;
    childInputValue.value = null;
    return;
  }

  // ✅ обновляем выбранного parent (child options)
  child.value = {
    value: rawParent.id,
    label: rawParent.name,
    children: rawParent.children,
  };

  // ✅ КРИТИЧНО: при смене parent сбросить выбранный child
  childInputValue.value = null;
}

// ✅ следим за parent v-model и перестраиваем child
watch(parentInputValue, (nv, ov) => {
  if (nv !== ov) getParent(nv);
});

// ✅ выставляем дефолты
onMounted(() => {
  if (props.defaultParentValue) {
    getParent(props.defaultParentValue);
    // если есть дефолтный child — ставим его
    if (props.defaultChildValue) {
      childInputValue.value = normalizeToId(props.defaultChildValue);
    }
  }
});

const validateField = async () => {
  console.log('Hello from NestedSelectField');

  const p = await parentValidate();
  const c = await childValidate();

  if (parentInputValue.value && childInputValue.value) {
    return {
      value: [parentInputValue.value, childInputValue.value],
      valid: p.valid && c.valid,
      mode: 'multi',
      property: props.property,
    };
  }

  return { value: undefined, valid: false, mode: 'multi', property: props.property };
};

defineExpose({ validateField });
</script>
